// Owner: interaction-kit (wave 1), design-system (wave 2). Extension point: keyboard commands and their
// scopes (registered in @dataroom/interaction at start-up) and command bar providers (rendered by the ⌘K
// host of shell-nav). Contribution files: `src/features/**/commands.ts`, default export
// `defineAppCommands({ id, scopes, commands, providers })`. These files load with the app shell, so keep
// them tiny: command `run` functions call into context the active scope provides, never import editors.
import type { AnyCommand, AnyScope, CommandContribution } from '@dataroom/interaction';
import type { IconName } from '@dataroom/ui';
import { createExtensionRegistry, hasShape } from './registry';

/** A result row of the command bar contributed by a feature (search is server-side; see core extensions). */
export type CommandBarItem = {
  id: string;
  title: string;
  subtitle?: string;
  icon?: IconName;
  /** Section heading in the command bar. */
  group: string;
  keywords?: readonly string[];
  /**
   * The words for exactly this item ("call", "note"): a query that equals one or starts it puts the item first,
   * above a record or an automation that merely has the word in its name.
   */
  verbs?: readonly string[];
  /** Navigate (an in-app path) or run. */
  href?: string;
  run?: () => void;
  /**
   * The item creates something ("New task", "New page"): it joins the command bar's Actions next to
   * "New <object>", whatever `group` says. Ids of the form `<feature>.new` or `<feature>.new.<kind>`
   * count as create items too.
   */
  create?: boolean;
  /**
   * The item is also offered while nothing is typed (the empty command bar's "Suggested"). Off by default:
   * the empty bar stays calm, only a feature that wants to be found without a search asks for it.
   */
  suggest?: boolean;
};

/** Whether a contributed item creates something (its `create` flag or a `*.new` / `*.new.*` id). */
export function isCreateItem(item: Pick<CommandBarItem, 'id' | 'create'>): boolean {
  return item.create ?? /(^|\.)new(\.|$)/.test(item.id);
}

export type CommandBarProvider = {
  /** Unique, dotted: `library.recent`, `automations.create`. */
  id: string;
  /** Items for a query (empty query = suggestions). Return fast; the host debounces and merges. */
  items: (
    query: string,
    ctx: {
      signal: AbortSignal;
      pathname: string;
      /** The bare `/` prefix: the member asks for every settings page, so return all of yours. */
      listAll?: boolean;
    },
  ) => CommandBarItem[] | Promise<CommandBarItem[]>;
};

export type AppCommandContribution = CommandContribution & {
  /** Unique id of the contributing feature (`workflows.builder`). */
  id: string;
  providers?: readonly CommandBarProvider[];
  /**
   * Loads what the titles of this feature's commands and scopes need: its deferred message catalog
   * (`() => import('./messages')`) or the module that fills its title table. `commands.ts` loads with
   * the app shell, but the feature's messages load with its screens, so a list of every command (the
   * Keyboard shortcuts page) awaits `ensureCommandTitles()` before it shows a title. Re-run on a
   * language switch, so it must be idempotent.
   */
  loadTitles?: () => Promise<unknown>;
};

export function defineAppCommands(def: {
  id: string;
  scopes?: readonly AnyScope[];
  commands?: readonly AnyCommand[];
  providers?: readonly CommandBarProvider[];
  loadTitles?: () => Promise<unknown>;
}): AppCommandContribution {
  return def;
}

export const commandContributions = createExtensionRegistry<AppCommandContribution>({
  point: 'commands',
  glob: 'features/**/commands.ts',
  host: 'shell-nav',
  modules: import.meta.glob('../features/**/commands.ts', { eager: true }),
  key: (c) => c.id,
  check: (v): v is AppCommandContribution => hasShape(v, ['id']),
});

/** Every command bar provider contributed by features. */
export function commandBarProviders(): CommandBarProvider[] {
  return commandContributions.list().flatMap((c) => [...(c.providers ?? [])]);
}

/** Loads the messages behind every contributed command and scope title (see `loadTitles`). */
export async function ensureCommandTitles(): Promise<void> {
  await Promise.all(commandContributions.list().map((c) => c.loadTitles?.()));
}
